import React, { useMemo, useState } from "react";
import { Check, Clipboard, RefreshCw, ShieldCheck } from "lucide-react";

type Props = {
  phrase: string;
  disabled?: boolean;
  generating?: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  onRegenerate: () => void;
};

const CONFIRMATION_POSITIONS = [1, 5, 10];

function normalizedWord(value: string) {
  return value.trim().toLowerCase();
}

export default function RecoveryPhraseSetup({
  phrase,
  disabled = false,
  generating = false,
  onConfirmedChange,
  onRegenerate,
}: Props) {
  const words = useMemo(() => phrase.trim().split(/\s+/).filter(Boolean), [phrase]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [copied, setCopied] = useState(false);

  const updateAnswer = (position: number, value: string) => {
    const next = { ...answers, [position]: value };
    setAnswers(next);
    const confirmed = CONFIRMATION_POSITIONS.every(
      (index) => normalizedWord(next[index] || "") === normalizedWord(words[index] || "")
    );
    onConfirmedChange(confirmed);
  };

  const copyPhrase = async () => {
    try {
      await navigator.clipboard.writeText(phrase);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-300/15 bg-amber-300/[0.05] p-3 text-xs font-semibold leading-5 text-amber-50/80">
        Save these words somewhere private. Blackspace stores only a secure verifier and cannot display this phrase again.
      </div>

      <div className="rounded-xl border border-white/10 bg-black/45 p-3.5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-cyan-100">
            <ShieldCheck size={14} /> Recovery phrase
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={copyPhrase}
              disabled={disabled || !phrase}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2.5 text-[11px] font-bold text-neutral-300 transition hover:text-white disabled:opacity-45"
            >
              {copied ? <Check size={13} /> : <Clipboard size={13} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={() => {
                setAnswers({});
                onConfirmedChange(false);
                onRegenerate();
              }}
              disabled={disabled || generating}
              aria-label="Generate a different recovery phrase"
              title="Generate a different recovery phrase"
              className="grid h-8 w-8 place-items-center rounded-md border border-white/10 bg-white/[0.04] text-neutral-400 transition hover:text-white disabled:opacity-45"
            >
              <RefreshCw size={13} className={generating ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <ol className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {words.map((word, index) => (
            <li key={`${index}-${word}`} className="flex min-w-0 items-center gap-2 rounded-lg bg-white/[0.035] px-2.5 py-2">
              <span className="w-4 shrink-0 text-right text-[10px] font-black text-neutral-600">{index + 1}</span>
              <span className="truncate text-xs font-bold text-neutral-100">{word}</span>
            </li>
          ))}
        </ol>
      </div>

      <div>
        <div className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-neutral-400">
          Confirm your saved phrase
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {CONFIRMATION_POSITIONS.map((position) => {
            const matches = normalizedWord(answers[position] || "") === normalizedWord(words[position] || "")
              && Boolean(answers[position]);
            return (
              <label key={position} className="rounded-lg border border-white/10 bg-black/35 px-3 py-2.5 focus-within:border-cyan-300/35">
                <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-neutral-600">
                  Word {position + 1}
                </span>
                <span className="mt-1 flex items-center gap-2">
                  <input
                    value={answers[position] || ""}
                    onChange={(event) => updateAnswer(position, event.target.value)}
                    disabled={disabled || !phrase}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white outline-none"
                  />
                  {matches && <Check size={14} className="shrink-0 text-emerald-300" />}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
